import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';
import { MotifTooltipModule } from '@ey-xd/ng-motif';

import { TaskDetailsConstants } from '@sa/app/shared/constants/ApplicationConstants';

/**
 * AssignedToComponent is an Angular standalone component used as a custom cell renderer for ag-Grid tables.
 * It displays information about the user or entity to whom a deliverable or task is assigned within a grid cell.
 * The component is designed for integration with ag-Grid, utilizing the provided cell renderer parameters to render assignment details.
 *
 * Implements ICellRendererAngularComp for ag-Grid compatibility.
 * Accepts ag-Grid cell renderer parameters to display assignment data.
 */
@Component({
  selector: 'app-assigned-to',
  standalone: true,
  imports: [CommonModule, MotifTooltipModule],
  templateUrl: './assigned-to.component.html',
  styleUrl: './assigned-to.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignedToComponent
  implements ICellRendererAngularComp, OnDestroy
{
  private cdr = inject(ChangeDetectorRef);
  private document = inject(DOCUMENT);

  public params: any = null;
  private tooltipEl: HTMLElement | null = null;

  protected readonly AssignedToConstants =
    TaskDetailsConstants.AssignedToConstants;

  /**
   * Initializes the component with the provided ag-Grid cell renderer parameters.
   *
   * @param params The ag-Grid cell renderer parameters containing assignment data.
   */
  public agInit(params: ICellRendererParams<any, any, any>): void {
    this.params = params;
  }

  /**
   * Refreshes the component with the new ag-Grid cell renderer parameters.
   *
   * @param params The ag-Grid cell renderer parameters containing updated assignment data.
   * @returns A boolean indicating whether the component was successfully refreshed.
   */
  public refresh(params: ICellRendererParams<any, any, any>): boolean {
    this.params = params;
    this.cdr.markForCheck();
    return true;
  }

  ngOnDestroy(): void {
    this.removeTooltip();
  }

  /**
   * Generates a tooltip string for the given rule subjects.
   *
   * @param subjects An array of rule subject strings.
   * @returns A formatted tooltip string containing the rule subjects.
   */
  protected getRuleSubjectsTooltip(subjects: string[]): string {
    return subjects.map(s => '• ' + s).join('\n');
  }

  /**
   * Handles the mouse enter event for the GTPw task icon.
   *
   * @param event The mouse event triggered when the cursor enters the GTPw task icon.
   */
  protected onGtPwMouseEnter(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top;

    this.removeTooltip();

    const el = this.document.createElement('div');
    el.className = 'gtpw-body-tooltip';

    const header = this.document.createElement('div');
    header.className = 'gtpw-body-tooltip__header';

    const title = this.document.createElement('span');
    title.textContent = 'GTPw Task';
    header.appendChild(title);

    header.insertAdjacentHTML(
      'beforeend',
      '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    );

    const name = this.document.createElement('div');
    name.className = 'gtpw-body-tooltip__name';
    name.textContent = String(this.params?.data?.gtPwTaskName ?? '');

    const arrow = this.document.createElement('div');
    arrow.className = 'gtpw-body-tooltip__arrow';

    el.appendChild(header);
    el.appendChild(name);
    el.appendChild(arrow);

    el.style.left = `${x}px`;
    el.style.top = `${y - 10}px`;

    this.document.body.appendChild(el);
    this.tooltipEl = el;
  }

  /**
   * Handles the mouse leave event for the GTPw task icon, removing the tooltip from the DOM.
   *
   * @param event The mouse event triggered when the cursor leaves the GTPw task icon.
   */
  protected onGtPwMouseLeave(): void {
    this.removeTooltip();
  }

  /**
   * Removes the tooltip element from the DOM if it exists, and resets the tooltip reference to null.
   */
  private removeTooltip(): void {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }
}
